/**
 * SiteFooter Organism Component
 * Footer with Instagram feed and social links
 */

'use client'

import { cn } from '@/lib/utils'
import { Facebook, Instagram, Linkedin } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'

interface SiteFooterProps {
	className?: string
}

// Sample Instagram posts data
const instagramPosts = [
	{
		id: '1',
		imageUrl:
			'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=500&h=500&fit=crop',
		link: 'https://www.instagram.com/tattersallcolombia/',
		caption: 'Tattersall Colombia',
		isVideo: false,
	},
	{
		id: '2',
		imageUrl:
			'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=500&h=500&fit=crop',
		link: 'https://www.instagram.com/tattersallcolombia/',
		caption: 'Tattersall Colombia',
		isVideo: false,
	},
	{
		id: '3',
		imageUrl:
			'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=500&h=500&fit=crop',
		link: 'https://www.instagram.com/tattersallcolombia/',
		caption: 'Tattersall Colombia',
		isVideo: false,
	},
	{
		id: '4',
		imageUrl:
			'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=500&h=500&fit=crop',
		link: 'https://www.instagram.com/tattersallcolombia/',
		caption: 'Tattersall Colombia',
		isVideo: true,
	},
	{
		id: '5',
		imageUrl:
			'https://images.unsplash.com/photo-1617814076367-b759c7d7e738?w=500&h=500&fit=crop',
		link: 'https://www.instagram.com/tattersallcolombia/',
		caption: 'Tattersall Colombia',
		isVideo: false,
	},
	{
		id: '6',
		imageUrl:
			'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=500&h=500&fit=crop',
		link: 'https://www.instagram.com/tattersallcolombia/',
		caption: 'Tattersall Colombia',
		isVideo: false,
	},
]

const footerLinks = {
	inicio: [
		{ label: 'Inicio', href: '/' },
		{ label: 'Nosotros', href: 'https://tattersall.com.co/nosotros/' },
		{ label: 'Nuestra flota', href: 'https://tattersall.com.co/nuestra-flota' },
		{ label: 'Para empresas', href: 'https://tattersall.com.co/para-empresas' },
		{ label: 'Para personas', href: 'https://tattersall.com.co/para-personas' },
		{ label: 'Sucursales', href: 'https://tattersall.com.co/sucursales' },
		{ label: 'Blog', href: 'https://tattersall.com.co/blog' },
		{
			label: 'Haz tu reserva aquí',
			href: '/',
		},
	],
	paraPersonas: [
		{
			label: 'Política tratamiento de datos',
			href: 'https://tattersall.com.co/politica-tratamiento-de-datos',
		},
		{
			label: 'Términos y condiciones',
			href: '/terminos-y-condiciones',
		},
	],
	paraEmpresas: [
		{
			label: 'Política tratamiento de datos',
			href: 'https://tattersall.com.co/politica-tratamiento-de-datos',
		},
		{
			label: 'Términos y condiciones',
			href: '/terminos-y-condiciones',
		},
	],
}

export function SiteFooter({ className }: SiteFooterProps) {
	return (
		<footer className={cn('w-full bg-white', className)}>
			{/* Instagram Section */}

			{/* Main Footer */}
			<div className='flex flex-col md:flex-row gap-0 py-16 px-6 md:py-[6%] md:px-[3%] bg-[#09194D]'>
				{/* Logo & Social */}
				<div className='flex flex-col justify-center md:w-[40%]'>
					<Image
						src='/images/logo-white.svg'
						alt='Tattersall Colombia'
						width={200}
						height={37}
						className='w-[75%] h-auto mb-12'
					/>

					{/* Social Icons */}
					<div className='flex items-start gap-7 text-center'>
						<Link
							href='https://instagram.com/tattersallcolombia'
							target='_blank'
							rel='noopener noreferrer'
							className='w-[27px] h-[27px] bg-white text-[#09194D] rounded-full flex items-center justify-center p-1.5 hover:bg-[#CDFF00] transition-colors'
							aria-label='Instagram'
						>
							<Instagram className='w-full h-full' />
						</Link>
						<Link
							href='https://linkedin.com/company/tattersallcolombia'
							target='_blank'
							rel='noopener noreferrer'
							className='w-[27px] h-[27px] bg-white text-[#09194D] rounded-full flex items-center justify-center p-1.5 hover:bg-[#CDFF00] transition-colors'
							aria-label='LinkedIn'
						>
							<Linkedin className='w-full h-full' />
						</Link>
						<Link
							href='https://twitter.com/tattersallcol'
							target='_blank'
							rel='noopener noreferrer'
							className='w-[27px] h-[27px] bg-white text-[#09194D] rounded-full flex items-center justify-center p-1.5 hover:bg-[#CDFF00] transition-colors'
							aria-label='Twitter'
						>
							<svg
								xmlns='http://www.w3.org/2000/svg'
								viewBox='0 0 24 24'
								fill='currentColor'
								className='w-full h-full'
							>
								<path d='M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' />
							</svg>
						</Link>
						<Link
							href='https://facebook.com/tattersallcolombia'
							target='_blank'
							rel='noopener noreferrer'
							className='w-[27px] h-[27px] bg-white text-[#09194D] rounded-full flex items-center justify-center p-1.5 hover:bg-[#CDFF00] transition-colors'
							aria-label='Facebook'
						>
							<Facebook className='w-full h-full' />
						</Link>
					</div>
				</div>

				{/* Navigation Links Column */}
				<div className='flex flex-col md:w-[25%]'>
					<nav>
						<ul className='space-y-1'>
							{footerLinks.inicio.map(link => (
								<li key={link.label}>
									<Link
										href={link.href}
										className='text-white font-["Bricolage_Grotesque"] text-lg font-black py-0.5 hover:text-[#CDFF00] transition-colors inline-block'
									>
										{link.label}
									</Link>
								</li>
							))}
						</ul>
					</nav>
				</div>

				{/* Para Personas Column */}
				<div className='flex flex-col md:w-[25%]'>
					<h3 className='font-["Bricolage_Grotesque"] text-[22px] font-black text-white mb-4'>
						PARA PERSONAS
					</h3>
					<nav className='-ml-4.5'>
						<ul className='space-y-1'>
							{footerLinks.paraPersonas.map(link => (
								<li key={link.href}>
									<Link
										href={link.href}
										className='text-white font-["Bricolage_Grotesque"] text-lg font-black py-0.5 hover:text-[#CDFF00] transition-colors inline-block'
									>
										{link.label}
									</Link>
								</li>
							))}
						</ul>
					</nav>
				</div>

				{/* Para Empresas Column */}
				<div className='flex flex-col md:w-[25%]'>
					<h3 className='font-["Bricolage_Grotesque"] text-[22px] font-black text-white mb-4'>
						PARA EMPRESAS
					</h3>
					<nav className='-ml-4.5'>
						<ul className='space-y-1'>
							{footerLinks.paraEmpresas.map(link => (
								<li key={link.href}>
									<Link
										href={link.href}
										className='text-white font-["Bricolage_Grotesque"] text-lg font-black py-0.5 hover:text-[#CDFF00] transition-colors inline-block'
									>
										{link.label}
									</Link>
								</li>
							))}
						</ul>
					</nav>
				</div>
			</div>

			{/* Copyright */}
			<div className='relative flex flex-col py-8 px-0 bg-[#CDFF00]'>
				<p className='text-center font-["Bricolage_Grotesque"] text-lg font-bold text-black'>
					©Tattersall colombia {new Date().getFullYear()} | Sitio web Diseñado con el alma y el
					corazón and powered by{' '}
					<Link
						href='https://gestionsystem.com.co'
						target='_blank'
						rel='noopener noreferrer'
						className='text-black hover:underline font-bold'
					>
						Gestion Systems
					</Link>
					<br />
					<span className='font-normal'>Todos los derechos reservados</span>
				</p>

				{/* WhatsApp Floating Button */}
				<Link
					href='https://wa.me/573001234567'
					target='_blank'
					rel='noopener noreferrer'
					className='fixed bottom-8 right-8 w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:bg-[#20BA5A] transition-colors z-50'
					aria-label='WhatsApp'
				>
					<svg
						xmlns='http://www.w3.org/2000/svg'
						viewBox='0 0 24 24'
						fill='white'
						className='w-8 h-8'
					>
						<path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z' />
					</svg>
				</Link>
			</div>
		</footer>
	)
}
